import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, Injector } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormArray, Validators, ValidatorFn } from '@angular/forms';
// services
import { MessageService } from '../../../../services/message.service';
import { MasterService } from '../../../../services/master.service';
import { StoreService } from '../../../../services/store.service';
import * as words from '../../../../services/language'
import { formatCurrency } from 'src/app/utils/format';
import { clean } from 'src/app/utils/numeric';
import { minValueValidator } from 'src/app/utils/min-value.validator';
import { ago } from 'src/app/utils/date';
import moment from 'moment';

@Component({
    selector: 'promise-edit',
    templateUrl: './promise-edit.component.html',
    styleUrls: ['./promise-edit.component.scss'],
    standalone: false
})
export class PayPromiseEditComponent implements OnInit, OnChanges {
    /*
    * variables
    */
    
    // The sale whose promises are being modified.
    @Input() sale: any = null
    
    // Currently selected language
    @Input() language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'
    
    // Word replacements for each language.
    @Input() words: Object = words.language

    // Communicate the completion of the editing process. Let the listener know if anything changed via the boolean.
    @Output() edited: EventEmitter<Boolean> = new EventEmitter()
    
    // Indicates whether the data is currently loading.
    protected loading: boolean = false
    
    protected form = new FormGroup({
        promises: this.fb.array([])
    })

    get promises() {
        return this.form.controls['promises'] as FormArray;
    }

    constructor(private master: MasterService, private ms: MessageService, private store: StoreService, private fb: FormBuilder) {
        this.form.get('promises').setValidators([this.totalPromisesValidator])
    }

    ngOnInit() {
        this.reset()
        this.fetch()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.sale) {
            this.reset()
            this.fetch()
        }
    }
    
    /*
    * functions
    */

    /**
     * Validates the total amount of promises against the past due amount.
     * @param items A list of promises to validate.
     */
    private totalPromisesValidator: ValidatorFn = (items: FormArray): { [key: string]: any } | null => {
        if (!items || !this.sale) {
            return null
        }

        const total = items.controls.reduce((sum, promise) => {
            const item = (promise as FormGroup).controls['amount']
            const value = clean(item.value)
            return sum + (isNaN(value) ? 0 : value)
        }, 0)

        const amountDue = parseFloat(this.sale?.amount || 0)
        if (total <= amountDue) {
            return null
        }

        return { 'totalExceedsDueAmount': { value: total } }
    }

    /**
     * Cancels the editing process.
     * @param e 
     */
    protected cancel = (e: Event): void => {
        e.stopPropagation()
        e.preventDefault()

        this.edited.next(false)
        this.reset()
    }
   
    /**
     * Saves the edited promises.
     * @param e 
     */
    protected save = (e: Event): void => {
        e.stopPropagation()
        e.preventDefault()

        if (this.form.invalid) {
            return
        }

        const adds = []
        let modified = false // at least one modification?
        for (let promise of this.promises.controls) {
            if (promise.pristine) { // not changed
                continue
            }

            const controls = (promise as FormGroup).controls
            
            const id = parseInt(controls.id.value)
            if (id && !isNaN(id) && (id > 0)) {
                this.modify(id, {
                    due: controls.due.value,
                    amount: controls.amount.value,
                    note: controls.note.value
                })

                modified = true
                continue
            }

            adds.push({
                due: controls.due.value,
                amount: clean(controls.amount.value),
                note: controls.note.value
            })
        }

        if (adds.length <= 0) {
            if (!modified) {
                this.edited.next(false)
            }

            this.reset()
            return
        }
        
        this.master.post(`sales/${this.sale['id']}/promises`, {promises: adds}, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }
            
            this.edited.next(true)
            this.reset()
        })
    }

    /**
     * Modifies an existing promise.
     */
    protected modify = (promiseId: number, payload: Object): void => {
        if (!promiseId || (promiseId <= 0) || !payload) {
            return
        }
        
        this.master.put(`payments/promises/${promiseId}`, payload, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.edited.next(true)
        })
    }

    /**
     * 
     */
    protected close = (promiseId: number, index: number): void => {
        if (!promiseId || (promiseId <= 0)) {
            return
        }
        
        this.master.patch(`payments/promises/${promiseId}/close`, null, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            if ((index < 0) || (index > this.promises.length - 1)) {
                return
            }
    
            this.promises.removeAt(index)
        })
    }

    /**
     * 
     */
    protected delete = (promiseId: number, index: number): void => {
        if (!promiseId || (promiseId <= 0)) {
            this.promises.removeAt(index)
            return
        }

        this.master.discard(`payments/promises/${promiseId}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            if ((index < 0) || (index > this.promises.length - 1)) {
                return
            }
    
            this.promises.removeAt(index)
            this.edited.next(true)
        })
    }

    /**
     * Add a new expected payment promise row to the form array
     * @param e Event that triggerd this method, if any
     */
    protected add = (e): void => {
        const promise = this.fb.group({
            id: new FormControl<number>(0, []),
            due: new FormControl<string | null>(null, [Validators.required]),
            amount: new FormControl<string>(formatCurrency(0), [Validators.required, minValueValidator(0.01)]),
            note: new FormControl<string | null>(null, [])
        })

        this.promises.push(promise)
    }

    /*
    * Reset the promises input array
    */
    private reset = (): void => {
        this.loading = false
        this.form.reset()
        this.promises.clear()
    }

    /**
     * 
     */
    private fetch() {
        if (this.loading) {
            return
        }

        if (!this.sale) {
            return
        }

        this.loading = true
        this.promises.clear()
        this.master.get(`sales/${this.sale['id']}/promises?closed=false`, res => {
            this.loading = false

            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }
            
            const results = res.data.promises
            if (!results || (results.length <= 0)) {
                this.add(null)
                this.preset()
                return
            }

            for (const item of results) {
                const promise = this.fb.group({
                    id: new FormControl<number>(item['id'], []),
                    due: new FormControl<string | null>(item.due, [Validators.required]),
                    amount: new FormControl<string>(formatCurrency(item.amount), [Validators.required, minValueValidator(0.01)]),
                    note: new FormControl<string | null>(item.note || null, [])
                })
                
                this.promises.push(promise)
            }

            this.preset()
        })
    }

    /**
     * Preset promise data if conditions are right
     */
    private preset() {
        if (!this.sale || !this.promises || (this.promises.length !== 1)) {
            return
        }

        const promise = this.promises.at(0)
        if (!promise) {
            return
        }

        const promiseId = parseInt(promise.get('id').value)
        if (promiseId && !isNaN(promiseId) && (promiseId > 0)) { // not a new promise
            return
        }

        const due = new Date(this.sale['due'])
        due.setDate(due.getDate() + (5-due.getDay() + 7)) // next Friday

        promise.patchValue({
            amount: formatCurrency(parseFloat(this.sale['amount'])),
            due: moment(due).format('YYYY-MM-DD')
        }, {emitEvent: false})
    }

    /**
     * Generate a color class (past, today, etc..) for the given due date.
     * @param due A date to generate a color class by.
     */
    protected dueToClass = (due: string): string => {
        return ago(due)
    }
}
